import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchApplicationDetails } from '../../../utils/apiUtils';
import styled from 'styled-components';
import { saveFormData } from '../../../utils/apiUtils';
import FilePreviewModal from '../../common/FilePreviewModal';
import SignatureCanvas from 'react-signature-canvas';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// A4 Dimensions (approximate for screen, maintaining aspect ratio)
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI
const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

// Helper function to convert Data URL to File object
const dataURLtoFile = (dataurl: string, filename: string): File | null => {
  try {
    const arr = dataurl.split(',');
    if (arr.length < 2) return null;
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch || mimeMatch.length < 2) return null;
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error('Error converting data URL to file:', error);
    return null;
  }
};

// Progress bar at top of page
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div<{ visible?: boolean }>`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  transition: opacity 0.3s ease;
  opacity: ${props => props.visible ? 1 : 0};
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545;
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000;
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px;
  background-color: white;
  padding: 50px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
  box-sizing: border-box;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #212529;
  font-size: 13px;
  line-height: 1.6;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const FormContentContainer = styled.div`
  /* Form content styling */
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

const NextPageButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 60px;
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

const SaveDraftButton = styled.button`
  background-color: #ffc107;
  color: #212529;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px;
  margin-right: 10px;
  display: inline-block;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px;
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

// Form specific styled components
const AppendixTitle = styled.h2`
  font-size: 1.4em;
  font-weight: 700;
  text-align: center;
  margin-bottom: 10px;
  color: #333;
`;

const SubTitle = styled.p`
  font-size: 0.95em;
  font-weight: 600;
  text-align: center;
  margin-bottom: 5px;
  color: #555;
`;

const AffidavitTitle = styled.h3`
  font-size: 1.1em;
  font-weight: 600;
  text-align: center;
  margin: 20px 0;
  text-decoration: underline;
  color: #333;
`;

const AffidavitContent = styled.div`
  margin-top: 30px;
  line-height: 2;
  font-size: 0.95em;
  text-align: justify;
`;

const InlineInput = styled.input`
  border: none;
  border-bottom: 1px solid #333;
  padding: 2px 5px;
  margin: 0 5px;
  font-size: inherit;
  font-family: inherit;
  min-width: 150px;
  background: transparent;
  
  &:focus {
    outline: none;
    border-bottom: 2px solid #6cb288;
  }
`;

const NumberedList = styled.ol`
  margin: 20px 0;
  padding-left: 30px;
  
  li {
    margin-bottom: 15px;
    line-height: 1.8;
    text-align: justify;
  }
`;

const SignatureSection = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const SignatureBlock = styled.div`
  text-align: center;
  min-width: 200px;
`;

const SignatureLabel = styled.p`
  margin-bottom: 10px;
  font-size: 0.9em;
`;

const SignatureContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadButton = styled.label`
  display: inline-block;
  padding: 8px 16px;
  background-color: #6cb288;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #4d9e70;
  }
`;

const TouchpadButton = styled.button`
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0069d9;
  }
`;

const SignaturePreview = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  min-width: 200px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9f9f9;
  margin-top: 10px;
`;

// Styled components for Signature Modal (Copied from Page 7 for consistency)
const SignatureModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050; // Higher than other elements
`;

const SignatureModalContent = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  width: auto;
  max-width: 90vw; // Ensure it fits on smaller screens
`;

const SignaturePadWrapper = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%; // Default width
  min-width: 300px; // Minimum width for the pad
  height: 200px; // Default height
  canvas {
    width: 100% !important; // Override inline styles of canvas if necessary
    height: 100% !important;
  }
`;

const SignatureModalActions = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
  margin-top: 10px;
`;

const SignatureButton = styled.button<{
  primary?: boolean;
  danger?: boolean;
  secondary?: boolean;
}>`
  padding: 10px 20px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.2s ease, transform 0.1s ease;

  ${props =>
    props.primary &&
    `
    background-color: #28a745; // Green for Save/Confirm
    color: white;
    &:hover { background-color: #218838; }
  `}

  ${props =>
    props.danger &&
    `
    background-color: #dc3545; // Red for Clear/Delete
    color: white;
    &:hover { background-color: #c82333; }
  `}

  ${props =>
    props.secondary &&
    `
    background-color: #6c757d; // Gray for Cancel/Close
    color: white;
    &:hover { background-color: #5a6268; }
  `}

  &:active {
    transform: scale(0.98);
  }
`;

interface FormData {
  applicationNoOfficeUse: string;
  affiantName: string;
  parentSpouseName: string;
  age: string;
  address: string;
  affidavitDay: string;
  affidavitMonth: string;
  affidavitYear: string;
  signatureFile: File | string | null;
  magistrateSignatureFile: File | string | null;
  errors?: {
    affiantName?: string;
    parentSpouseName?: string;
    age?: string;
    address?: string;
    affidavitDay?: string;
    affidavitMonth?: string;
    affidavitYear?: string;
    signatureFile?: string;
    magistrateSignatureFile?: string;
  };
}

interface ApplicationFormPage8Props {
  formData?: any;
  isViewMode?: boolean;
}

const initialState: FormData = {
  applicationNoOfficeUse: '',
  affiantName: '',
  parentSpouseName: '',
  age: '',
  address: '',
  affidavitDay: '',
  affidavitMonth: '',
  affidavitYear: '',
  signatureFile: null,
  magistrateSignatureFile: null,
  errors: {}
};

const ApplicationFormPage8: React.FC<ApplicationFormPage8Props> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [applicationId, setApplicationId] = useState<string>('');
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  
  // Progress bar state
  const [formProgress, setFormProgress] = useState<number>(0);
  
  // Zoom state
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  
  // Fetch showDummyDataButton setting from backend
  useEffect(() => {
    const fetchDummyDataSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
        }
      } catch (error) {
        console.error('Error fetching dummy data button setting:', error);
      }
    };
    fetchDummyDataSetting();
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>(propFormData || { ...initialState });

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);

  // Signature Pad Modal State and Ref
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const signaturePadRef = useRef<SignatureCanvas>(null);
  const [isMagistrateSignatureModalOpen, setIsMagistrateSignatureModalOpen] = useState(false);
  const magistrateSignaturePadRef = useRef<SignatureCanvas>(null);
  const [signatureImageUrl, setSignatureImageUrl] = useState<string | null>(null);
  const [magistrateSignatureImageUrl, setMagistrateSignatureImageUrl] = useState<string | null>(null);
  
  // Save timeout reference for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // PDF Generation State
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  // Effect to initialize or generate applicationId
  useEffect(() => {
    if (propFormData?.applicationNoOfficeUse) {
      setApplicationId(propFormData.applicationNoOfficeUse);
      if (typeof propFormData.signatureFile === 'string' && propFormData.signatureFile) {
        const sigPath = propFormData.signatureFile.startsWith('/uploads/') ? propFormData.signatureFile : `/uploads/${propFormData.signatureFile}`;
        setSignatureImageUrl(`${API_BASE_URL}${sigPath}`);
      }
      if (typeof propFormData.magistrateSignatureFile === 'string' && propFormData.magistrateSignatureFile) {
        const magSigPath = propFormData.magistrateSignatureFile.startsWith('/uploads/') ? propFormData.magistrateSignatureFile : `/uploads/${propFormData.magistrateSignatureFile}`;
        setMagistrateSignatureImageUrl(`${API_BASE_URL}${magSigPath}`);
      }
      return;
    }

    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[ApplicationFormPage8 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
      if (formData.applicationNoOfficeUse !== currentApplicationId) {
        setFormData(prev => ({ ...prev, applicationNoOfficeUse: currentApplicationId }));
      }
    } else {
      const newId = generateApplicationId();
      console.log(`[ApplicationFormPage8 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true;
      setFormData(prev => ({ ...prev, applicationNoOfficeUse: newId }));
    }
  }, [location.state, propFormData]);

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    if (propFormData) return; // Skip loading if formData is passed as a prop

    const loadDraftData = async () => {
      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[ApplicationFormPage8 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        try {
          const appData = await fetchApplicationDetails(applicationId);
          if (appData && appData.formData) {
            const loadedFormData = {
              ...initialState,
              ...appData.formData,
            };
            setFormData(loadedFormData);
            if (typeof loadedFormData.signatureFile === 'string' && loadedFormData.signatureFile) {
              const sigPath = loadedFormData.signatureFile.startsWith('/uploads/') ? loadedFormData.signatureFile : `/uploads/${loadedFormData.signatureFile}`;
              setSignatureImageUrl(`${API_BASE_URL}${sigPath}`);
            } else {
              setSignatureImageUrl(null);
            }
            if (typeof loadedFormData.magistrateSignatureFile === 'string' && loadedFormData.magistrateSignatureFile) {
              const magSigPath = loadedFormData.magistrateSignatureFile.startsWith('/uploads/') ? loadedFormData.magistrateSignatureFile : `/uploads/${loadedFormData.magistrateSignatureFile}`;
              setMagistrateSignatureImageUrl(`${API_BASE_URL}${magSigPath}`);
            } else {
              setMagistrateSignatureImageUrl(null);
            }
            console.log(`[ApplicationFormPage8 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
          } else {
            console.warn(`[ApplicationFormPage8 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
          }
        } catch (error) {
          console.error(`[ApplicationFormPage8 LoadDraft] Error loading draft data for ${applicationId}:`, error);
        }
      }
    };

    loadDraftData();
  }, [applicationId, isViewMode, propFormData]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleOpenFilePreview = (file: File | string | null, defaultFileName: string = 'file') => {
    console.log('[handleOpenFilePreview] Called with file:', file, 'defaultFileName:', defaultFileName);
    if (!file) {
      console.log('[handleOpenFilePreview] File is null, returning.');
      return;
    }

    console.log('[handleOpenFilePreview] Current API_BASE_URL:', API_BASE_URL);

    let url: string | null = null;
    let name: string | null = null;
    let type: string | null = null;

    if (typeof file === 'string') {
      console.log('[handleOpenFilePreview] File is a string:', file);

      // Determine backend origin consistently using REACT_APP_API_URL if absolute; otherwise fallback to current origin
      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const urlObject = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${urlObject.protocol}//${urlObject.host}`;
        } catch (e) {
          console.warn('[handleOpenFilePreview] Could not parse REACT_APP_API_URL, using window.origin instead:', e);
          serverBaseForFiles = window.location.origin;
        }
      } else {
        // Relative API URL - check if we're in development
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isDevelopment && window.location.port === '3000') {
          serverBaseForFiles = `${window.location.protocol}//${window.location.hostname}:5000`;
        }
      }

      // Normalize to a path (strip origin, ensure leading slash)
      let normalizedPath = file;
      if (file.includes('://')) {
        try { normalizedPath = new URL(file).pathname; } catch { normalizedPath = file; }
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = normalizedPath.includes('/') ? `/${normalizedPath}` : `/uploads/${normalizedPath}`;
      }

      url = `${serverBaseForFiles}${normalizedPath}`;
      name = normalizedPath.split('/').pop() || defaultFileName;

      const lower = name.toLowerCase();
      if (lower.endsWith('.pdf')) {
        type = 'application/pdf';
      } else if (lower.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const ext = lower.split('.').pop();
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else {
        type = 'application/octet-stream';
      }
      console.log(`[handleOpenFilePreview] Constructed URL: ${url}, Name: ${name}, Type: ${type}`);
    } else {
      console.log('[handleOpenFilePreview] File is a File object:', file);
      url = URL.createObjectURL(file);
      name = file.name;
      type = file.type;
      console.log('[handleOpenFilePreview] File object - Constructed URL:', url, 'Name:', name, 'Type:', type);
    }

    setModalFileUrl(url);
    setModalFileName(name);
    setModalFileType(type);
    setIsModalOpen(true);
    console.log('[handleOpenFilePreview] Modal state set. isOpen:', true, 'Url:', url);
  };

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    try {
      const formElement = formRef.current;
      if (!formElement) {
        alert('Form content is not available for PDF export.');
        setIsPdfGenerating(false);
        return;
      }

      const formClone = formElement.cloneNode(true) as HTMLElement;

      formClone.style.position = 'absolute';
      formClone.style.left = '-9999px';
      formClone.style.top = '0px';
      formClone.style.width = `${formElement.offsetWidth}px`;
      document.body.appendChild(formClone);

      const inputs = formClone.querySelectorAll('input[type="text"], input[type="number"], textarea');
      inputs.forEach(input => {
        const typedInput = input as HTMLInputElement | HTMLTextAreaElement;
        const value = typedInput.value;
        if (value) {
          const span = document.createElement('span');
          span.textContent = value;
          span.style.borderBottom = '1px solid #333';
          span.style.padding = '2px 5px';
          span.style.margin = '0 5px';
          span.style.fontSize = 'inherit';
          span.style.fontFamily = 'inherit';
          span.style.display = 'inline-block';
          span.style.whiteSpace = 'pre-wrap';
          span.style.wordBreak = 'break-word';
          span.style.color = window.getComputedStyle(typedInput).color;
          typedInput.parentNode?.replaceChild(span, typedInput);
        }
      });

      const checkboxes = formClone.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      checkboxes.forEach(checkbox => {
        const typedCheckbox = checkbox as HTMLInputElement;
        const span = document.createElement('span');
        span.textContent = typedCheckbox.checked ? '☑' : '☐';
        span.style.fontSize = 'inherit';
        span.style.fontFamily = 'inherit';
        span.style.display = 'inline-block';
        span.style.margin = '0 5px';
        typedCheckbox.parentNode?.replaceChild(span, typedCheckbox);
      });

      const buttons = formClone.querySelectorAll('button');
      buttons.forEach(btn => (btn.style.visibility = 'hidden'));

      const canvas = await html2canvas(formClone, {
        scale: 2,
        useCORS: true,
        width: formClone.scrollWidth,
        height: formClone.scrollHeight,
        windowWidth: formClone.scrollWidth,
        windowHeight: formClone.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.5); // Use aggressive JPEG compression
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'l' : 'p',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      
      // Simulate compression process for visual feedback
      for (let i = 0; i <= 100; i += 10) {
        setCompressionProgress(i);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      pdf.save(`application_form_page8_${applicationId || 'draft'}.pdf`);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert(`Error generating PDF for Page 8. Please check the console for details. Error: ${error instanceof Error ? error.message : String(error)}`);
      // Ensure buttons are visible even if an error occurs
      const formClone = document.querySelector('body > *:last-child') as HTMLElement;
      if (formClone) {
        const buttons = formClone.querySelectorAll('button');
        buttons.forEach(btn => btn.style.visibility = 'visible');
      }
    } finally {
      const formClone = document.querySelector('body > *:last-child') as HTMLElement;
      if (formClone && formClone.parentNode === document.body) {
        document.body.removeChild(formClone);
      }
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };

  // Function to save data to database
  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;

    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      return;
    }

    if (!isOnline) {
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.');
      return;
    }

    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    
    try {
      const { errors, ...payloadData } = data; // Exclude errors from the data to be saved

      let finalPayload: any;

      if (payloadData.signatureFile instanceof File || payloadData.magistrateSignatureFile instanceof File) {
        finalPayload = new FormData();
        // Append all keys from payloadData
        for (const key in payloadData) {
          if (Object.prototype.hasOwnProperty.call(payloadData, key)) {
            const value = payloadData[key as keyof typeof payloadData];
            if (key === 'signatureFile' && value instanceof File) {
              finalPayload.append(key, value, value.name);
            } else if (key === 'magistrateSignatureFile' && value instanceof File) {
              finalPayload.append(key, value, value.name);
            } else if (value !== null && value !== undefined) {
              finalPayload.append(key, String(value)); // FormData expects string or Blob/File
            }
          }
        }
      } else {
        // If signatureFile is not a File (e.g., string path from server, or null),
        // send the payloadData as a plain object. apiUtils.saveFormData will JSON.stringify it.
        finalPayload = payloadData;
      }
      
      // console.log('[SaveToDB] Final payload being sent:', finalPayload);
      // For FormData objects, logging finalPayload directly might not be very readable in console.
      // If finalPayload is FormData, you might iterate through entries for better logging if needed:
      // if (finalPayload instanceof FormData) { 
      //   for (let [key, value] of finalPayload.entries()) { 
      //     console.log(`[SaveToDB FormData Entry] ${key}:`, value); 
      //   } 
      // } else { 
      //   console.log('[SaveToDB JSON Payload]:', finalPayload); 
      // }

      const response = await saveFormData(idToUse, finalPayload, isDraft);
      
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false;
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
      } else {
        setSaveMessage('Error saving');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setShowSaveIndicator(false), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline]);

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline && Object.keys(formData).some(key => formData[key as keyof FormData] !== '' && key !== 'errors' && key !== 'applicationNoOfficeUse')) {
      console.log(`[ApplicationFormPage8 Effect AutoSave] Application ID ${applicationId} is newly generated. Attempting to save as draft.`);
      saveToDatabaseCallback(formData, true, applicationId);
    }
  }, [applicationId, formData, saveToDatabaseCallback, isOnline]);

  const handleFillDummyData = () => {
    console.log('[ApplicationFormPage8 FillDummyData] Filling form with dummy data.');
    const currentDate = new Date();
    setFormData(prevData => ({
      ...prevData,
      applicationNoOfficeUse: prevData.applicationNoOfficeUse || applicationId || '',
      affiantName: 'John Doe',
      parentSpouseName: 'Robert Doe',
      age: '35',
      address: '123 Main Street, Pune, Maharashtra 411001',
      affidavitDay: currentDate.getDate().toString(),
      affidavitMonth: currentDate.toLocaleString('default', { month: 'long' }),
      errors: {},
    }));
    setSaveMessage('Dummy data filled. Save to persist.');
    setShowSaveIndicator(true);
    setTimeout(() => {
      setShowSaveIndicator(false);
    }, 3000);
  };

  const debouncedSave = useCallback((newData: FormData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      if (applicationId) {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        saveToDatabaseCallback({ ...newData, applicationNoOfficeUse: applicationId }, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}`);
      }
    }, 1000);
  }, [saveToDatabaseCallback, applicationId]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  const handleSignatureFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const updatedFormData = { ...formData, signatureFile: file };
      setFormData(updatedFormData);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setSignatureImageUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setSignatureImageUrl(null); // Clear preview if not an image (e.g. PDF)
      }
      
    } else {
      // Optionally handle case where user cancels file dialog
      // setFormData(prev => ({ ...prev, signatureFile: null }));
      // setSignatureImageUrl(null);
      // debouncedSave({ ...formData, signatureFile: null });
    }
  };

  const handleMagistrateSignatureFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const updatedFormData = { ...formData, magistrateSignatureFile: file };
      setFormData(updatedFormData);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setMagistrateSignatureImageUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setMagistrateSignatureImageUrl(null); // Clear preview for non-image files like PDF
      }
    } else {
      setFormData({ ...formData, magistrateSignatureFile: null });
      setMagistrateSignatureImageUrl(null);
    }
  };

  const handleTouchpadSign = () => {
    setIsSignatureModalOpen(true);
  };

  const handleClearSignature = () => {
    signaturePadRef.current?.clear();
    setSignatureImageUrl(null); // Clear the image preview
    // If clearing the pad should also remove the file from formData:
    // setFormData(prev => ({ ...prev, signatureFile: null }));
    // If auto-save is desired after clearing:
    // debouncedSave({ ...formData, signatureFile: null }); 
  };

  const handleSaveSignature = () => {
    if (signaturePadRef.current) {
      if (signaturePadRef.current.isEmpty()) {
        alert('Please provide a signature first.');
        return;
      }
      const signatureDataUrl = signaturePadRef.current.toDataURL('image/png');
      const signatureFilename = `signature-${applicationId || Date.now()}.png`;
      const signatureFile = dataURLtoFile(signatureDataUrl, signatureFilename);
      setSignatureImageUrl(signatureDataUrl); // Set preview from canvas

      if (signatureFile) {
        const updatedFormData = { ...formData, signatureFile: signatureFile };
        setFormData(updatedFormData);
      } else {
        alert('Could not save signature. Please try again.');
      }
      setIsSignatureModalOpen(false);
    }
  };

  const handleMagistrateTouchpadSign = () => {
    setIsMagistrateSignatureModalOpen(true);
  };

  const handleClearMagistrateSignature = () => {
    magistrateSignaturePadRef.current?.clear();
    setMagistrateSignatureImageUrl(null);
  };

  const handleSaveMagistrateSignature = () => {
    if (magistrateSignaturePadRef.current) {
      if (magistrateSignaturePadRef.current.isEmpty()) {
        alert('Please provide the magistrate signature first.');
        return;
      }
      const signatureDataUrl = magistrateSignaturePadRef.current.toDataURL('image/png');
      setMagistrateSignatureImageUrl(signatureDataUrl);
      const signatureFileName = `magistrate-signature-${applicationId || Date.now()}.png`;
      const signatureFile = dataURLtoFile(signatureDataUrl, signatureFileName);

      if (signatureFile) {
        const updatedFormData = { ...formData, magistrateSignatureFile: signatureFile };
        setFormData(updatedFormData);
        debouncedSave(updatedFormData);
      }
      setIsMagistrateSignatureModalOpen(false);
    }
  };

  const handleZoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  };
  
  const handleZoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.1, 0.6));
  };

  const handleNextButtonClick = async () => {
    // Save draft before navigation
    if (applicationId && isOnline) {
      console.log('[Navigation Page8] Saving draft before navigating');
      await saveToDatabaseCallback({ ...formData, applicationNoOfficeUse: applicationId }, true, applicationId);
    }
    if (applicationId && applicationId !== formData.applicationNoOfficeUse) {
      setFormData(prev => ({
        ...prev,
        applicationNoOfficeUse: applicationId
      }));
    }
    navigate('/form/page9');
  };

  // Calculate form progress whenever form data changes
  useEffect(() => {
    const totalFields = 7; // 7 fields including signature
    let filledFields = 0;
    
    if (formData.affiantName && formData.affiantName.trim() !== '') filledFields++;
    if (formData.parentSpouseName && formData.parentSpouseName.trim() !== '') filledFields++;
    if (formData.age && formData.age.trim() !== '') filledFields++;
    if (formData.address && formData.address.trim() !== '') filledFields++;
    if (formData.affidavitDay && formData.affidavitDay.trim() !== '') filledFields++;
    if (formData.affidavitMonth && formData.affidavitMonth.trim() !== '') filledFields++;
    if (formData.signatureFile) filledFields++;

    const progressPercentage = totalFields > 0 ? (filledFields / totalFields) * 100 : 0;
    setFormProgress(progressPercentage);
  }, [formData]);

  // Effect for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getSignatureFileName = (): string => {
    if (!formData.signatureFile) return 'No signature uploaded';
    if (typeof formData.signatureFile === 'string') return formData.signatureFile;
    return formData.signatureFile.name;
  };

  const getMagistrateSignatureFileName = (): string => {
    if (formData.magistrateSignatureFile instanceof File) {
      return formData.magistrateSignatureFile.name;
    }
    if (typeof formData.magistrateSignatureFile === 'string') {
      return formData.magistrateSignatureFile;
    }
    return 'No magistrate signature file selected.';
  };

  const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  `;

  const ModalContent = styled.div`
    background: white;
    padding: 20px;
    border-radius: 8px;
    text-align: center;
    max-width: 400px;
    width: 90%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  `;

  const ProgressBarContainerModal = styled.div`
    width: 100%;
    height: 20px;
    background-color: #e9ecef;
    border-radius: 10px;
    margin-top: 20px;
    overflow: hidden;
  `;

  const ProgressBarFillModal = styled.div<{ progress: number }>`
    height: 100%;
    width: ${props => props.progress}%; // Dynamic width based on progress
    background-color: #6cb288;
    transition: width 0.3s ease-in-out;
  `;

  const ProgressText = styled.p`
    margin-top: 10px;
    font-size: 0.9em;
    color: #555;
  `;

  return (
    <>
      {/* Progress bar */}
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel visible={true}>Page 8/14 • {Math.round(formProgress)}% complete</ProgressLabel>
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      
      <FloatingBackButton onClick={() => navigate('/form/page7')} aria-label="Back to previous page">
        ←
      </FloatingBackButton>
      
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      
      <FormPageNavigator currentPage={8} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} />
      
      <div style={{ position: 'fixed', right: '-25px', top: '605px', zIndex: 999 }}>
        <NextPageButton 
          onClick={handleNextButtonClick}
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px'
          }}
        >
          Next Page 9/14 →
        </NextPageButton>
      </div>
      
      <PageWrapper scale={zoomScale}>
        <A4PageContainer ref={formRef}>
          <FormContentContainer>
            <AppendixTitle>APPENDIX - A</AppendixTitle>
            <SubTitle>(TO BE TYPED ON APPROPRIATE NON-JUDICIAL STAMP PAPER OF REQUIRED VALUE)</SubTitle>
            <AffidavitTitle>AFFIDAVIT FOR INDIVIDUALS & PARTNERS</AffidavitTitle>
            
            <AffidavitContent>
              <p>
                I,
                <InlineInput
                  type="text"
                  name="affiantName"
                  value={formData.affiantName}
                  onChange={handleChange}
                  placeholder="Full Name"
                  style={{ minWidth: '250px' }}
                />
                son/daughter/wife of
                <InlineInput
                  type="text"
                  name="parentSpouseName"
                  value={formData.parentSpouseName}
                  onChange={handleChange}
                  placeholder="Parent/Spouse Name"
                  style={{ minWidth: '250px' }}
                />
                Age
                <InlineInput
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  placeholder="Age"
                  style={{ minWidth: '60px', maxWidth: '80px' }}
                />
                years residing at
                <InlineInput
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Complete Address"
                  style={{ minWidth: '400px' }}
                />
                do hereby solemnly affirm and say as under:
              </p>

              <NumberedList>
                <li>That I am an Indian National.</li>
                <li>That I am married/unmarried.</li>
                <li>I have never been convicted and/or no process has been issued and/or no charges have been framed by a Court of law against me for any criminal offence involving moral turpitude/ economic offences (other than a traffic offence).</li>
                <li>I hereby confirm that I was/ I never a signatory to a dealership/distributorship agreement of any oil company, which was terminated for proven adulteration/malpractices.</li>
                <li>That details of various lands as and when required for setting up and operation of the Dealership have been furnished. I undertake that these funds will be made available for the purpose stated above. In case it is found that the same is not made available as and when required, the offer of Dealership, at any stage, shall be withdrawn and that I will have no claim/damages whatsoever against Maharashtra Natural Gas Limited.</li>
                <li>I hereby verify that what has been stated above is true to the best of my knowledge and belief and that no material fact has been concealed thereat. If any information/ declaration given by me in my application or in any document submitted by me/us in support of application for the award of Dealership is found to be untrue or incorrect or false, Maharashtra Natural Gas Limited would be within its rights to withdraw the letter of intent/ offer of Dealership, Maharashtra Natural Gas Limited would be within its rights to withdraw the letter of intent/ offer of Dealership (if already appointed) and that I/we would have no claim, whatsoever, against Maharashtra Natural Gas Limited or its employees for such withdrawal/termination.</li>
              </NumberedList>

              <p style={{ marginTop: '30px', marginBottom: '40px' }}>
                Solemnly affirmed and declared before me
              </p>

              <p>
                This
                <InlineInput
                  type="text" // Or number, if you prefer strict number input
                  name="affidavitDay"
                  value={formData.affidavitDay}
                  onChange={handleChange}
                  placeholder="DD"
                  style={{ width: '50px', textAlign: 'center' }} // Adjusted width
                />
                day of
                <InlineInput
                  type="text"
                  name="affidavitMonth"
                  value={formData.affidavitMonth}
                  onChange={handleChange}
                  placeholder="Month"
                  style={{ width: '100px', textAlign: 'center' }} // Adjusted width
                />
                <InlineInput
                  type="text" // Or number
                  name="affidavitYear"
                  value={formData.affidavitYear}
                  onChange={handleChange}
                  placeholder="YYYY"
                  style={{ width: '70px', textAlign: 'center' }} // Adjusted width
                />
              </p>
            </AffidavitContent>

            <SignatureSection>
              <SignatureBlock>
                {/* Magistrate Signature IMAGE PREVIEW - Above 'Signature and Seal of' */}
                {magistrateSignatureImageUrl && (
                  <div id="magistrate-signature-display-area" style={{ marginBottom: '10px', border: '1px solid #ddd', padding: '5px', display: 'inline-block' }}>
                    <img 
                      src={magistrateSignatureImageUrl} 
                      alt="Magistrate Signature Preview" 
                      style={{ display: 'block', maxWidth: '200px', maxHeight: '100px', height: 'auto' }} 
                    />
                  </div>
                )}
                <SignatureLabel>Signature and Seal of</SignatureLabel>
                <SignatureLabel>Magistrate/Judge/Notary public</SignatureLabel>
                {/* Magistrate Signature CONTROLS - Below 'Magistrate/Judge/Notary public' */}
                <div className="signature-controls" style={{ display: 'flex', gap: '10px', marginTop: '10px', marginBottom: '10px' }}>
                  <FileUploadButton htmlFor="magistrateSignatureFile">
                    Upload Magistrate Signature
                    <FileInput
                      type="file"
                      id="magistrateSignatureFile"
                      accept="image/*,.pdf"
                      onChange={handleMagistrateSignatureFileChange}
                      disabled={isViewMode}
                    />
                  </FileUploadButton>
                  <TouchpadButton onClick={handleMagistrateTouchpadSign} disabled={isViewMode}>
                    Sign using Touchpad (Magistrate)
                  </TouchpadButton>
                </div>
                {formData.magistrateSignatureFile && (
                  <SignaturePreview style={{ marginBottom: '10px' }}>
                    <span style={{ fontSize: '0.9em', color: '#666', marginRight: '10px' }}>
                      {getMagistrateSignatureFileName()}
                    </span>
                    <button 
                      onClick={() => handleOpenFilePreview(formData.magistrateSignatureFile, 'Magistrate Signature')}
                      style={{ fontSize: '0.8em' }}
                      type="button"
                      disabled={isViewMode}
                    >
                      Preview Magistrate Signature
                    </button>
                  </SignaturePreview>
                )}
              </SignatureBlock>
              
              <SignatureBlock>
                {signatureImageUrl && (
                  <div id="affiant-signature-display-area" style={{ marginBottom: '10px', border: '1px solid #ddd', padding: '5px', display: 'inline-block' }}>
                    <img 
                      src={signatureImageUrl} 
                      alt="Applicant Signature" 
                      style={{ display: 'block', maxWidth: '200px', maxHeight: '100px', height: 'auto' }} 
                    />
                  </div>
                )}
                <SignatureLabel>Signatory of person making Affidavit</SignatureLabel>
                <SignatureLabel>(Name in block letters):</SignatureLabel>
                <SignatureContainer>
                  <div className="signature-controls" style={{ display: 'flex', gap: '10px' }}>
                    <FileUploadButton htmlFor="signatureFile">
                      Upload Signature
                      <FileInput
                        type="file"
                        id="signatureFile"
                        accept="image/*,.pdf"
                        onChange={handleSignatureFileChange}
                      />
                    </FileUploadButton>
                    <TouchpadButton onClick={handleTouchpadSign}>
                      Sign using Touchpad
                    </TouchpadButton>
                  </div>
                  {formData.signatureFile && (
                    <SignaturePreview>
                      <span style={{ fontSize: '0.9em', color: '#666' }}>
                        {getSignatureFileName()}
                      </span>
                      <button 
                        onClick={() => handleOpenFilePreview(formData.signatureFile, 'Signature')} 
                        style={{ marginLeft: '10px', fontSize: '0.8em' }}
                      >
                        Preview
                      </button>
                    </SignaturePreview>
                  )}
                </SignatureContainer>
              </SignatureBlock>
            </SignatureSection>
          </FormContentContainer>
          <PageNumber>8</PageNumber>
        </A4PageContainer>
        
        <FilePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          fileUrl={modalFileUrl}
          fileName={modalFileName}
          fileType={modalFileType}
        />
        {isSignatureModalOpen && (
          <SignatureModalOverlay>
            <SignatureModalContent>
              <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>Draw your signature</h5>
              <SignaturePadWrapper>
                <SignatureCanvas
                  ref={signaturePadRef}
                  penColor='black'
                  canvasProps={{
                    style: { width: '100%', height: '100%', border: '1px solid #dee2e6' },
                  }}
                />
              </SignaturePadWrapper>
              <SignatureModalActions>
                <SignatureButton danger onClick={handleClearSignature}>Clear</SignatureButton>
                <SignatureButton secondary onClick={() => setIsSignatureModalOpen(false)}>Cancel</SignatureButton>
                <SignatureButton primary onClick={handleSaveSignature}>Save Signature</SignatureButton>
              </SignatureModalActions>
            </SignatureModalContent>
          </SignatureModalOverlay>
        )}

        {/* Magistrate Signature Modal */}
        {isMagistrateSignatureModalOpen && (
          <SignatureModalOverlay>
            <SignatureModalContent>
              <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>Draw Magistrate's Signature</h5>
              <SignaturePadWrapper id="magistrate-signature-wrapper">
                <SignatureCanvas
                  ref={magistrateSignaturePadRef}
                  penColor='black'
                  canvasProps={{
                    style: { width: '100%', height: '100%', border: '1px solid #dee2e6' },
                  }}
                />
              </SignaturePadWrapper>
              <SignatureModalActions>
                <SignatureButton danger onClick={handleClearMagistrateSignature}>Clear</SignatureButton>
                <SignatureButton secondary onClick={() => setIsMagistrateSignatureModalOpen(false)}>Cancel</SignatureButton>
                <SignatureButton primary onClick={handleSaveMagistrateSignature}>Save Signature</SignatureButton>
              </SignatureModalActions>
            </SignatureModalContent>
          </SignatureModalOverlay>
        )}
        
        {isPdfGenerating && (
          <ModalOverlay>
            <ModalContent>
              <h3>Generating PDF</h3>
              <p>Please wait... Generating PDF</p>
              <ProgressBarContainerModal>
                <ProgressBarFillModal progress={compressionProgress} />
              </ProgressBarContainerModal>
              <ProgressText>Compression: {compressionProgress}%</ProgressText>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageWrapper>
      
      {/* Empty ActionButtonsContainer to maintain structure */}
      <ActionButtonsContainer style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '800px', zIndex: 1000 }}>
        {/* Next Page button moved near FormPageNavigator */}
      </ActionButtonsContainer>
      
      {/* Session Timer */}
      {!isViewMode && (
        <SessionTimer 
          sessionDuration={30}
          warningTime={5}
          onSessionExpired={() => navigate('/')}
          onSaveState={async () => {
            if (applicationId) {
              await saveToDatabaseCallback(formData, true, applicationId);
            }
          }}
        />
      )}

      {/* Home Button - Above Download PDF */}
      <button
        onClick={() => navigate('/user-dashboard')}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '95px',
          bottom: isViewMode ? '100px' : '175px',
          zIndex: 1000,
          width: '145px',
          height: '45px',
          borderRadius: '50px',
          border: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '13px',
          fontWeight: '600',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
          transition: 'all 0.2s ease'
        }}
      >
        🏠 Dashboard
      </button>

      {!isViewMode && (
        <>
          <PayNowButton 
            onClick={handleDownloadPdf} 
            disabled={!isOnline || isViewMode}
            style={{ position: 'fixed', left: '95px', bottom: '105px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
          >
            DOWNLOAD PDF
          </PayNowButton>
          
          <SaveDraftButton 
            onClick={() => saveToDatabaseCallback(formData, true, applicationId)} 
            disabled={isSaving || !applicationId || !isOnline}
            style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.2px' }}
          >
            {isSaving && saveMessage.startsWith('Saving Draft') ? 'Saving...' : 'SAVE DRAFT'}
          </SaveDraftButton>
        </>
      )}
      
      {isViewMode && (
        <PayNowButton 
          onClick={handleDownloadPdf} 
          disabled={!isOnline}
          style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
        > 
          DOWNLOAD PDF
        </PayNowButton>
      )}
    </>
  );
};

// Helper to make all props of a styled component optional for the 'as' prop or other scenarios
type Optional<T> = { [P in keyof T]+?: T[P] };

export default ApplicationFormPage8;